import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';

import { ChartViewType } from './sidebar/types';
import { IAnalyticMainProps } from './types';
import { sanitizeFormValues } from './utils/sanitizeFormValues';
import { AnalyticsDataProvider } from './context/AnalyticsDataContext';
import { AnalyticsContent } from './AnalyticsContent';

interface ExtendedAnalyticMainProps extends IAnalyticMainProps {
  globalCurrency?: string;
}

export const AnalyticsContainerMain: FC<ExtendedAnalyticMainProps> = (props) => {
  const { hashedFilter, setHashedFilter, globalCurrency } = props;

  // Chart view state management
  const [selectedChartView, setSelectedChartView] = useState<ChartViewType>(hashedFilter ? hashedFilter.filters?.selectedChartView : ChartViewType.TREND);

  // Form setup
  const formContext = useForm({
    defaultValues: {
      ...hashedFilter.filters.formValues,
      name: hashedFilter.name,
      description: hashedFilter.description,
      // Override currencyReference to ensure USD is default if no currency is set
      // This must come AFTER the spread to override any existing value
      ...(hashedFilter.filters.formValues?.currencyReference ? {} : { currencyReference: 'USD' }),
    },
  });

  const { watch } = formContext;
  const { name, description, ...formValues } = watch();

  // Extract table filters from saved report (if any)
  const initialTableFilters = useMemo(() => hashedFilter?.tableFilters, [hashedFilter]);

  // Base search request (for Key Metrics & Tables - includes granularity as required by API)
  const baseSearchRequest = useMemo(
    () => ({
      ...sanitizeFormValues(formValues),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(formValues)]
  );

  // Chart request (same as base request for now, kept separate for future customization if needed)
  const chartSearchRequest = useMemo(
    () => ({
      ...baseSearchRequest,
    }),
    [baseSearchRequest]
  );

  return (
    <FormContainer formContext={formContext}>
      <AnalyticsDataProvider baseSearchRequest={baseSearchRequest} chartSearchRequest={chartSearchRequest} initialTableFilters={initialTableFilters}>
        <AnalyticsContent
          hashedFilter={hashedFilter}
          setHashedFilter={setHashedFilter}
          globalCurrency={globalCurrency}
          selectedChartView={selectedChartView}
          setSelectedChartView={setSelectedChartView}
        />
      </AnalyticsDataProvider>
    </FormContainer>
  );
};
